/* eslint-disable no-mixed-operators */
import { from, Subscriber } from "rxjs";
import {
  isEmpty,
  isNil,
  mergeAll,
  map,
  compose,
  head,
  last,
  objOf,
  split,
  replace,
  equals,
  not,
  and,
  filter,
  length,
} from "ramda";
import { ajax } from "rxjs/ajax";

import {
  RESPONSE_TYPE,
  JSON_CONTENT_TYPE,
  UIGP_CHANNEL_SECURITY_HEADER,
  INTERNET,
} from "../constants";
import app_config from "./../../utils/config";
import { store } from "../../storeExporter";
import { setTriggerErrorModal, timeExpired } from "../../App/module/actions";

const throwError = (response) => {
  let error;
  if (response !== null) {
    error = Error(response.errorCode || response.status);
    error.response = response;
    throw error;
  }
};

const checkStatus = (response) => {
  if (response.status === 401) store.dispatch(timeExpired(true));
  if (response.status === 400 || response.status > 404)
    store.dispatch(setTriggerErrorModal({ status: response.status }));
  if (response && response.ok) {
    return response;
  }
  if (response && response.status === 204) {
    return response;
  }
  if (response && response.status === 201) {
    return response;
  }

  throwError(response);
};

const checkErrorCode = (response) => {
  if (response && (isNil(response.errorCode) || isEmpty(response.errorCode))) {
    return response;
  }

  throwError(response);
};

const parseResponse = (response, type) => {
  const internetHeader =
    response.headers && response.headers.get(UIGP_CHANNEL_SECURITY_HEADER);

  if (response.status === 200) store.dispatch(timeExpired(false));
  if (response.status === 401) store.dispatch(timeExpired(true));
  if (response.status === 204) return null;
  if (response.status === 201) return null;

  if (type === RESPONSE_TYPE.BLOB) {
    return response.blob();
  }
  if (type === RESPONSE_TYPE.OCTET_STREAM) {
    return response;
  }
  if (type === RESPONSE_TYPE.BASE_64) {
    return response;
  }
  if (type === RESPONSE_TYPE.TEXT_PLAIN) {
    return response;
  }
  return response.json();
};

const _apiPort = `:${app_config.apiPort}`;

export const _apiBase = `${app_config.apiProtocol}//${app_config.apiAddress}${_apiPort}${app_config.apiSegment}`;

const _setDefaultHeader = (headers, name, defaultValue) => {
  if (!headers.has(name)) {
    headers.set(name, defaultValue);
  }
};

export const paramsToObject = (queryParams) =>
  compose(
    mergeAll,
    map((value) => objOf(head(value), decodeURIComponent(last(value)))),
    filter((value) => and(equals(length(value), 2), not(isEmpty(head(value))))),
    map(split("=")),
    split("&"),
    replace("?", "")
  )(queryParams);

const _objectToParams = (obj) =>
  Object.keys(obj)
    .map((value) => `${value}=${obj[value]}`)
    .join("&");

const _observer = (
  requestType,
  endpoint,
  headers,
  body,
  responseType,
  acceptType,
  contentType
) => {
  let params =
    (requestType === "GET" && !isEmpty(body) && `?${_objectToParams(body)}`) ||
    "";

  let fetchParams = {
    url: `${_apiBase}/${endpoint}${params}`,
    init: {
      method: requestType,
      headers: _prepareHeaders(headers, acceptType, contentType),
    },
  };

  if (requestType !== "GET") {
    if (contentType === "application/json") {
      fetchParams.init.body =
        typeof body === "object" ? JSON.stringify(body) : body;
    } else {
      fetchParams.init.body = body;
    }
  }

  // eslint-disable-next-line no-undef
  let request = () => fetch(fetchParams.url, fetchParams.init);

  return from(
    request()
      .then(checkStatus)
      .then((response) => parseResponse(response, responseType))
      .then(checkErrorCode)
  );
};

const _mockObserver = (
  requestType,
  endpoint,
  headers,
  body,
  responseType,
  acceptType,
  contentType
) => {
  let params =
    (requestType === "GET" && !isEmpty(body) && `?${_objectToParams(body)}`) ||
    "";

  let fetchParams = {
    url: endpoint,
    init: {
      method: requestType,
      headers: _mockPrepareHeaders(headers, acceptType, contentType),
    },
  };

  if (requestType !== "GET") {
    if (contentType === "application/json") {
      fetchParams.init.body =
        typeof body === "object" ? JSON.stringify(body) : body;
    } else {
      fetchParams.init.body = body;
    }
  }

  // eslint-disable-next-line no-undef
  let request = () => fetch(fetchParams.url, fetchParams.init);

  return from(
    request()
      .then(checkStatus)
      .then((response) => parseResponse(response, responseType))
      .then(checkErrorCode)
  );
};

const _mockPrepareHeaders = (headers, acceptType, contentType) => {
  let result = headers || new Headers();
  if (!(result instanceof Headers)) {
    result = new Headers(result);
  }

  _setDefaultHeader(result, "Accept", acceptType);
  if (contentType && contentType !== "multipart/form-data")
    _setDefaultHeader(result, "Content-Type", contentType);
  _setDefaultHeader(result, "Access-Control-Allow-Origin", "*");

  return result;
};

const _prepareHeaders = (headers, acceptType, contentType) => {
  let result = headers || new Headers();
  if (!(result instanceof Headers)) {
    result = new Headers(result);
  }

  _setDefaultHeader(result, "Accept", acceptType);
  if (contentType && contentType !== "multipart/form-data")
    _setDefaultHeader(result, "Content-Type", contentType);
  _setDefaultHeader(result, "Access-Control-Allow-Origin", "*");

  const token = sessionStorage.getItem("token");
  if (token && token !== "") _setDefaultHeader(result, "Authorization", token);

  return result;
};

const ajaxCall = (options) => {
  const requestOptions = { ...options };
  if (window.oAuth2.getAccessToken()) {
    if (!requestOptions.headers) {
      requestOptions.headers = {};
    }
    requestOptions.headers.Authorization = `Bearer ${window.oAuth2.getAccessToken()}`;
  }

  return ajax(requestOptions)
    .map((data) => data.response)
    .catch((error) => {
      if (error.status === 401) {
        return window.oAuth2.oauthWindow.open();
      }
      return error;
    });
};

export const publicGET = (
  endpoint,
  body = {},
  headers = {},
  responseType = RESPONSE_TYPE.JSON,
  acceptType = JSON_CONTENT_TYPE,
  contentType = JSON_CONTENT_TYPE
) =>
  _observer(
    "GET",
    endpoint,
    headers,
    body,
    responseType,
    acceptType,
    contentType
  );

export const publicPOST = (
  endpoint,
  body = {},
  headers = {},
  responseType = RESPONSE_TYPE.JSON,
  acceptType = JSON_CONTENT_TYPE,
  contentType = JSON_CONTENT_TYPE
) =>
  _observer(
    "POST",
    endpoint,
    headers,
    body,
    responseType,
    acceptType,
    contentType
  );

export const publicPUT = (
  endpoint,
  body = {},
  headers = {},
  responseType = RESPONSE_TYPE.JSON,
  acceptType = JSON_CONTENT_TYPE,
  contentType = JSON_CONTENT_TYPE
) =>
  _observer(
    "PUT",
    endpoint,
    headers,
    body,
    responseType,
    acceptType,
    contentType
  );

export const publicDELETE = (
  endpoint,
  body = {},
  headers = {},
  responseType = RESPONSE_TYPE.JSON,
  acceptType = JSON_CONTENT_TYPE,
  contentType = JSON_CONTENT_TYPE
) =>
  _observer(
    "DELETE",
    endpoint,
    headers,
    body,
    responseType,
    acceptType,
    contentType
  );

export const GET = (
  endpoint,
  body = {},
  headers = {},
  responseType = RESPONSE_TYPE.JSON,
  acceptType = JSON_CONTENT_TYPE,
  contentType = JSON_CONTENT_TYPE
) =>
  _observer(
    "GET",
    endpoint,
    headers,
    body,
    responseType,
    acceptType,
    contentType
  );

export const MOCK_GET = (
  endpoint,
  body = {},
  headers = {},
  responseType = RESPONSE_TYPE.JSON,
  acceptType = JSON_CONTENT_TYPE,
  contentType = JSON_CONTENT_TYPE
) =>
  _mockObserver(
    "GET",
    endpoint,
    headers,
    body,
    responseType,
    acceptType,
    contentType
  );

export const POST = (
  endpoint,
  body = {},
  headers = {},
  responseType = RESPONSE_TYPE.JSON,
  acceptType = JSON_CONTENT_TYPE,
  contentType = JSON_CONTENT_TYPE
) =>
  _observer(
    "POST",
    endpoint,
    headers,
    body,
    responseType,
    acceptType,
    contentType
  );

export const PUT = (
  endpoint,
  body = {},
  headers = {},
  responseType = RESPONSE_TYPE.JSON,
  acceptType = JSON_CONTENT_TYPE,
  contentType = JSON_CONTENT_TYPE
) =>
  _observer(
    "PUT",
    endpoint,
    headers,
    body,
    responseType,
    acceptType,
    contentType
  );

export const PATCH = (
  endpoint,
  body = {},
  headers = {},
  responseType = RESPONSE_TYPE.JSON,
  acceptType = JSON_CONTENT_TYPE,
  contentType = JSON_CONTENT_TYPE
) =>
  _observer(
    "PATCH",
    endpoint,
    headers,
    body,
    responseType,
    acceptType,
    contentType
  );

export const DELETE = (
  endpoint,
  body = {},
  headers = {},
  responseType = RESPONSE_TYPE.JSON,
  acceptType = JSON_CONTENT_TYPE,
  contentType = JSON_CONTENT_TYPE
) =>
  _observer(
    "DELETE",
    endpoint,
    headers,
    body,
    responseType,
    acceptType,
    contentType
  );

export const upload = (endpoint, method, body, onProgress) => {
  const progressSubscriber = Subscriber.create(onProgress, () => {});
  const url = `${_apiBase}/${endpoint}`;
  const requestOptions = {
    url,
    body,
    method,
    progressSubscriber,
  };
  return ajaxCall(requestOptions);
};

export const mockGET = (
  endpoint,
  body = {},
  headers = {},
  responseType = RESPONSE_TYPE.JSON
) => mockResponse(endpoint, body, headers, responseType);

export const mockPOST = (
  endpoint,
  body = {},
  headers = {},
  responseType = RESPONSE_TYPE.JSON
) => mockResponse(endpoint, body, headers, responseType);

export const mockPUT = (
  endpoint,
  body = {},
  headers = {},
  responseType = RESPONSE_TYPE.JSON
) => mockResponse(endpoint, body, headers, responseType);

export const mockPATCH = (
  endpoint,
  body = {},
  headers = {},
  responseType = RESPONSE_TYPE.JSON
) => mockResponse(endpoint, body, headers, responseType);

export const mockDELETE = (
  endpoint,
  body = {},
  headers = {},
  responseType = RESPONSE_TYPE.JSON
) => mockResponse(endpoint, body, headers, responseType);

export const mockResponse =
  (endpoint, body, headers, responseType) =>
  (mockResponse = { status: "success", data: null }, responseTime = 500) => {
    const response = {
      ...mockResponse,
      requestParams: {
        endpoint,
        body,
        headers,
        responseType,
      },
    };
    return from(
      new Promise((resolve, reject) => {
        if (response.status === "success")
          setTimeout(() => resolve(response), responseTime);
        else setTimeout(() => reject(response), responseTime);
      })
    );
  };
